import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import FileSearchService from '../../services/FileSearchService';
import PostService from '../../services/PostService';
import { getAuthHeaders } from '../../utils/authUtils';

const MIN_SEARCH = 2;
const SEARCH_DELAY_MS = 300;

/**
 * The Video box on New post and Edit post: finds a video by title. Under it, the picked languages
 * the video doesn't have yet, so the PM can fix that first. value: { videoId, title } or null.
 * languages: [{ value, label }]. value.removed: the video was deleted after it was picked.
 */
const VideoPicker = ({ value, onChange, languages }) => {
    const [text, setText] = useState('');
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchFailed, setSearchFailed] = useState(false);
    const [onVideo, setOnVideo] = useState(null);
    const [lookupFailed, setLookupFailed] = useState(false);
    const videoId = value?.videoId;
    const removed = !!value?.removed;

    useEffect(() => {
        if (text.trim().length < MIN_SEARCH || text === value?.title) {
            setOptions([]);
            return undefined;
        }
        let alive = true;
        const timer = setTimeout(async () => {
            setSearching(true);
            setSearchFailed(false);
            try {
                const { email, auth } = getAuthHeaders();
                const found = await FileSearchService.advancedSearch({ query: text.trim(), email, auth });
                if (alive) setOptions(found.videoFiles.map((f) => ({ videoId: f.data, title: f.name, path: f.path })));
            } catch (e) {
                if (alive) {
                    setOptions([]);
                    setSearchFailed(true);
                }
            } finally {
                if (alive) setSearching(false);
            }
        }, SEARCH_DELAY_MS);
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [text, value?.title]);

    useEffect(() => {
        setOnVideo(null);
        setLookupFailed(false);
        if (!videoId || removed) return undefined;
        let alive = true;
        PostService.video(videoId)
            .then((v) => alive && setOnVideo(v.languageKeys))
            .catch(() => alive && setLookupFailed(true));
        return () => {
            alive = false;
        };
    }, [videoId, removed]);

    const missing = onVideo ? languages.filter((l) => !onVideo.includes(l.value)) : [];

    return (
        <Box>
            <Autocomplete
                size="small"
                options={options}
                value={value}
                filterOptions={(x) => x}
                getOptionLabel={(o) => o.title || ''}
                isOptionEqualToValue={(a, b) => a.videoId === b.videoId}
                noOptionsText={
                    text.trim().length < MIN_SEARCH ? 'Type the title' : searchFailed ? "Couldn't search. Try again." : 'No video found'
                }
                onChange={(_, v) => onChange(v)}
                onInputChange={(_, v) => setText(v)}
                renderOption={(props, o) => (
                    <li {...props} key={o.videoId}>
                        <Box>
                            <Typography variant="body2">{o.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {o.path}
                            </Typography>
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Video (optional)"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {searching && <CircularProgress size={16} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            {(removed || lookupFailed || missing.length > 0) && (
                <Typography variant="caption" color="warning.main" component="div" sx={{ mt: 0.5 }} role="status">
                    {removed
                        ? 'This video was deleted. Pick another or clear it.'
                        : lookupFailed
                          ? "Couldn't check which languages this video has."
                          : `Not on this video yet: ${missing.map((l) => l.label).join(', ')}.`}
                </Typography>
            )}
        </Box>
    );
};

export default VideoPicker;
